/**
 * What each warning category shows in its detail drawer.
 *
 * Every field here reads a key the reconciliation response ALREADY carries — the
 * warning arrays are complete detail rows, not counts, and the overview's numbers
 * are simply their lengths. Nothing in this file derives, guesses or supplements
 * a value; a field whose source is absent renders as absent.
 *
 * Categories differ in which keys they populate (`ReconWarningItem` fills only
 * the fields relevant to its `warning_type`), which is why this is a per-category
 * list rather than one shared column set. Claiming a column a category does not
 * fill would print an empty cell that reads like a missing value rather than an
 * inapplicable one.
 */
import {
  INVOICE_STATUS_LABEL,
  PROVIDER_MISSING_LABEL,
  fmtMoney,
  mapLabel,
} from "../bexioFormat";

const num = (v) => (v == null || v === "" ? null : String(v));

/** Money, or nothing — `fmtMoney` renders "—" for null, which we do not want. */
const money = (v, currency) => (v == null || v === "" ? null : fmtMoney(v, currency));

const status = (v) => (v ? mapLabel(INVOICE_STATUS_LABEL, v) : null);

/**
 * The backend's own vocabulary for the duplicate-risk date window. These are
 * labels for values the service already computes — no new classification.
 */
const DATE_WINDOW_LABEL = {
  inside_90d_window: "Innerhalb 90-Tage-Fenster",
  outside_90d_window: "Ausserhalb 90-Tage-Fenster",
  no_issue_date: "Kein Rechnungsdatum",
  unknown_billing_period: "Abrechnungsperiode unbekannt",
};

/** `warning_type` on an excluded row IS the exclusion reason the service assigned. */
const EXCLUDED_REASON_LABEL = {
  mapped_canceled: "Storniert",
  mapped_draft: "Entwurf",
  // The reason PM-1 added, and the only one here that is not a Bexio status: the
  // invoice may still say "pending" and simply no longer exist at the provider.
  // Until this entry existed the card rendered the raw `mapped_provider_missing`,
  // because unknown reasons pass through verbatim below.
  mapped_provider_missing: PROVIDER_MISSING_LABEL,
};

/**
 * The difference between the invoice's `total_paid` and the sum of its payment
 * rows — computed here ONLY when both numbers are actually present, because a
 * difference against a missing operand is not a difference.
 */
function paidDelta(item, currency) {
  if (item?.total_paid == null || item?.payment_sum == null) return null;
  const a = Number(item.total_paid);
  const b = Number(item.payment_sum);
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return fmtMoney(a - b, currency);
}

/** Primary identifier of a row — what the operator looks up in Bexio. */
export function warningRowTitle(item) {
  return (
    item?.document_nr ||
    item?.bexio_document_nr ||
    (item?.bexio_invoice_id != null ? `#${item.bexio_invoice_id}` : "Ohne Beleg-Nr.")
  );
}

/**
 * Per category: the fields to show, in reading order.
 *
 * `get` returns a display string or null. Null means "this category does not
 * carry that value" and the field is left out entirely.
 */
export const WARNING_FIELDS = {
  canceled_with_payment_rows: [
    { label: "Status", get: (i) => status(i.bexio_status) },
    { label: "Zahlungszeilen", get: (i) => num(i.payment_rows) },
    { label: "Zahlungssumme", get: (i, c) => money(i.payment_sum, c) },
    { label: "total_paid", get: (i, c) => money(i.total_paid, c) },
  ],
  total_paid_differs_from_payment_sum: [
    { label: "total_paid", get: (i, c) => money(i.total_paid, c) },
    { label: "Summe Zahlungszeilen", get: (i, c) => money(i.payment_sum, c) },
    { label: "Differenz", get: paidDelta },
    { label: "Zahlungszeilen", get: (i) => num(i.payment_rows) },
  ],
  paid_without_payment_rows: [
    { label: "Status", get: (i) => status(i.bexio_status) },
    { label: "total_paid", get: (i, c) => money(i.total_paid, c) },
    { label: "Zahlungszeilen", get: (i) => num(i.payment_rows) },
  ],
  pending_with_payment_rows: [
    { label: "Status", get: (i) => status(i.bexio_status) },
    { label: "Zahlungszeilen", get: (i) => num(i.payment_rows) },
    { label: "Zahlungssumme", get: (i, c) => money(i.payment_sum, c) },
    { label: "Offener Rest", get: (i, c) => money(i.total_remaining, c) },
  ],
  excluded_mapped_invoices: [
    {
      label: "Ausschlussgrund",
      // The service's own `warning_type`; unknown values pass through verbatim
      // rather than being mapped to something invented.
      get: (i) => EXCLUDED_REASON_LABEL[i.warning_type] || i.warning_type || null,
    },
    { label: "Status", get: (i) => status(i.bexio_status) },
    { label: "Betrag", get: (i, c) => money(i.total, c) },
    { label: "Tenancy", get: (i) => i.tenancy_id || null },
  ],
  invalid_mapped_invoices: [
    { label: "Status", get: (i) => status(i.bexio_status) },
    { label: "Betrag", get: (i, c) => money(i.total, c) },
    { label: "Tenancy", get: (i) => i.tenancy_id || null },
    {
      label: "Nicht auflösbar",
      get: (i) =>
        Array.isArray(i.unresolved_fields) && i.unresolved_fields.length
          ? i.unresolved_fields.join(", ")
          : null,
    },
  ],
  duplicate_risk_vantio_invoices: [
    { label: "Bexio-Betrag", get: (i, c) => money(i.bexio_total, c) },
    { label: "Vantio-Rechnung", get: (i) => i.vantio_invoice_number || null },
    { label: "Vantio-Betrag", get: (i, c) => money(i.vantio_amount, c) },
    // Existing logic supplies this; it is not recomputed here.
    { label: "Delta", get: (i, c) => money(i.amount_diff, c) },
    {
      label: "Zeitfenster",
      get: (i) => DATE_WINDOW_LABEL[i.date_window] || i.date_window || null,
    },
    { label: "Tenancy", get: (i) => i.tenancy_id || null },
  ],
};

/** The backend's own sentence for a row, where it wrote one. */
export function warningRowDetail(item) {
  return item?.detail || item?.unresolved_reason || null;
}

/** Fields with a value, for this category and this row. */
export function visibleWarningFields(categoryKey, item, currency) {
  const spec = WARNING_FIELDS[categoryKey] || [];
  return spec
    .map((f) => ({ label: f.label, value: f.get(item, currency) }))
    .filter((f) => f.value != null && f.value !== "");
}
